import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Line as ChartLine } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import styles from 'components/Chart/Chart/Chart.module.scss'
import { externalTooltipHandler } from 'components/Chart/Chart/externalTooltipHandler'

const ANIMATION_DURATION = 1 //sec
const verticalLinePlugin = {
  id: 'verticalLinePlugin',
  beforeDraw: function (chart, args, options) {
    const {
      ctx,
      chartArea: { bottom },
    } = chart
    ctx.save()

    const index = chart._active.length > 0 ? chart._active[0].index : null

    if (index) {
      ctx.beginPath()
      ctx.moveTo(chart._active[0].element.x, bottom)
      ctx.strokeStyle = options.lineColor ? options.lineColor : 'transparent'
      ctx.lineTo(chart._active[0].element.x, 10)
      ctx.stroke()
      ctx.restore()
    }
  },
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  verticalLinePlugin,
)

const options = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    verticalLinePlugin: {
      lineColor: '#E3E5E9',
    },
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: externalTooltipHandler,
    },
  },

  interaction: {
    mode: 'index',
    intersect: false,
  },

  scales: {
    x: {
      grid: {
        drawBorder: false,
        display: false,
      },
      ticks: {
        color: '#1E2F4B',
        font: {
          size: 11,
        },
      },
    },
    y0: {
      display: false,
      grid: {
        display: false,
      },
      position: 'left',
      type: 'linear',
    },
    y1: {
      display: false,
      grid: {
        display: false,
      },
      position: 'right',
      type: 'linear',
    },
    y2: {
      display: false,
      grid: {
        display: false,
      },
      position: 'left',
      type: 'linear',
    },
    y3: {
      display: false,
      grid: {
        display: false,
      },
      position: 'right',
      type: 'linear',
    },
  },
}

export const Line = ({
  items = [],
  metrics = [],
  labels = null,
  include_vat,
  measure = {},
  isMobile = false,
  colors = [],
}) => {
  const { t } = useTranslation(['pulse'])

  const mobileEvents = useMemo(
    () => (isMobile ? { events: ['click', 'mouseenter'] } : {}),
    [isMobile],
  )

  if (!items.length || !metrics.length || !labels) {
    return null
  }

  const data = {
    labels,
    datasets: metrics.map(({ id, isHidden }, index) => {
      const hasChildren = id.indexOf('.') > -1
      let currentLabel
      if (hasChildren) {
        const [key, value] = id.split('.')
        currentLabel = t(`chart.legend.labels.${key}.${value}`)
      } else {
        currentLabel = t(`chart.legend.labels.${id}`)
      }

      return {
        labelSuffix: measure[id] || '',
        label: currentLabel,
        borderColor: colors[index],
        backgroundColor: colors[index],
        yAxisID: 'y' + index,
        data: items.map((item) => {
          let currentItem
          if (hasChildren) {
            const [key, value] = id.split('.')
            currentItem = item?.[key]?.[value] ?? { value: 0 }
          } else {
            currentItem = item?.[id] ?? { value: 0 }
          }
          return include_vat && currentItem.valueIncludeVat
            ? currentItem.valueIncludeVat
            : currentItem.value
        }),
        hidden: !!isHidden,
        fill: false,
      }
    }),
  }

  return (
    <>
      <div
        className={styles['chart__progressive-shadow']}
        style={{ animationDuration: ANIMATION_DURATION + 's' }}
      />
      <ChartLine
        data-is-mobile={isMobile}
        type="line"
        options={{ ...options, ...mobileEvents }}
        data={data}
      />
    </>
  )
}

Line.defaultProps = {
  items: [],
  metrics: [],
  labels: null,
  include_vat: false,
  measure: {},
  isMobile: false,
  colors: [],
}

Line.propTypes = {
  items: PropTypes.array,
  metrics: PropTypes.array,
  labels: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  include_vat: PropTypes.bool,
  measure: PropTypes.object,
  isMobile: PropTypes.bool,
  colors: PropTypes.array,
}
